import { isValidJWT } from "./isValidJWT";
import { ROUTES } from "constants/paths";

/**
 * Session persistence for the two b2b2c portals.
 *
 * Both portals previously held auth in memory only, so a refresh signed the
 * user out. This stores the login response so a reload can rehydrate.
 *
 ── Why sessionStorage, not localStorage ────────────────────────────────────
 * Scoped to the tab and cleared when it closes, so neither credential sits on
 * disk between sessions the way the legacy admin token does in localStorage.
 *
 * Since S-204 the ACCESS token expires in minutes, so the exposure that matters
 * here is the REFRESH token. It is rotating and revocable — a stolen one stops
 * working the moment the legitimate holder refreshes, because the server sees the
 * replay and revokes the whole family — but for its window it is the long-lived
 * credential, and script on this origin can still read it.
 *
 * The remaining hardening is to move the refresh token into an httpOnly cookie
 * scoped to the refresh path, which the page cannot read at all. That needs a
 * server-side Set-Cookie and was left as a deliberate, scoped exception to the
 * "no cookies" rule rather than done unilaterally.
 *
 * ── What is stored, and what it is worth ────────────────────────────────────
 * `permissions` and `role` are cached so the shell can render without a second
 * round trip — the server offers no "me"/refresh-context endpoint, so login is
 * the only time they arrive. They drive AFFORDANCE only: what a menu shows.
 * They are client-side data and a determined user can edit them, so they must
 * never be treated as the authorization boundary. The server enforces that.
 */

/** One key per portal so signing into one never clobbers the other's session. */
const KEYS = {
  crm: "_zenvoya_crm_session",
  partner: "_zenvoya_partner_session",
};

const keyFor = (portal) => KEYS[portal] || KEYS.crm;

/**
 * Which portal the page is in, derived from the route.
 *
 * Each portal keeps its OWN session under its own key above, so "which session
 * does this request belong to" is a fact about where the user IS, not about the
 * API module being called. Deriving it centrally is what lets the modules shared
 * by both portals — bookingsAPI, spec/reference — work from either one.
 *
 * They were not doing that. Every shared module calls `postToBackend(path, body)`
 * with no third argument, so it took the `"crm"` default, read the CRM session
 * while the user was signed into the PARTNER portal, found nothing, and sent an
 * empty `x-access-token`. The request still carried `x-api-key`, so it reached
 * the route and was refused for having no identity — indistinguishable, from the
 * screen, from the portal simply being broken. Nothing surfaced the real cause,
 * because the transport has no notion of "signed in to the wrong portal".
 *
 * Lowercased before comparing: react-router matches case-insensitively, so
 * `/partneradmin/bookings` renders the partner portal exactly as
 * `/partnerAdmin/bookings` does, and a case-sensitive prefix test would send the
 * CRM session for the first one.
 *
 * A module that belongs to ONE portal still passes its portal explicitly
 * (spec/partner-*.js, crmActingPartnerAPI) and that argument always wins. Only
 * genuinely shared modules should rely on this.
 */
export const activePortal = () => {
  if (typeof window === "undefined") return "crm";
  const path = window.location.pathname.toLowerCase();
  return path.startsWith(ROUTES.partnerAdmin.toLowerCase()) ? "partner" : "crm";
};

/**
 * Persist a successful login.
 *
 * `jwtToken` is stored verbatim: it is AES ciphertext and must be echoed back
 * as x-access-token exactly as received. Decrypting it here, or prefixing it
 * with "Bearer", produces a token the server cannot verify.
 */
export const savePortalSession = (portal, data) => {
  try {
    sessionStorage.setItem(
      keyFor(portal),
      JSON.stringify({
        jwtToken: data?.jwtToken || "",
        // Session lifecycle (S-204). The access token now expires in minutes;
        // this is what renews it. Rotation means the value CHANGES on every
        // refresh, so it must be re-saved each time or the next refresh replays a
        // consumed token — which the server reads as theft and revokes the whole
        // family, signing the user out.
        refreshToken: data?.refreshToken || "",
        accessTokenExpiresAt: data?.accessTokenExpiresAt || null,
        user: data?.user || null,
        partner: data?.partner || null,
        branches: data?.branches || [],
        /**
         * The branch this session chose to work at, partner console only.
         *
         * Stored because NOTHING stores it server-side: `/partner/auth/branch`
         * mints a token carrying the branch and keeps no record, so `/refresh`
         * — which re-reads the user row — answers the user's own branch again.
         * This is the memory that lets the portal re-apply the choice after a
         * refresh. `null` means "the partner's default", which is what every
         * session is until someone picks.
         */
        actingBranchCode: data?.actingBranchCode ?? null,
        role: data?.role || null,
        permissions: data?.permissions || [],
        modules: data?.modules || [],
        /**
         * What the TENANT sells and what it is entitled to — as opposed to
         * `permissions` above, which is what this PERSON may do. The portal ANDs
         * them: a user can hold a Bookings grant for a vertical their tenant
         * does not sell.
         *
         * ⚠️ `features` uses `??`, not `||`, and that is the point. The server
         * sends `null` for a tenant whose plan is not configured, meaning
         * "unknown — conclude nothing", which is NOT the same as `[]` meaning
         * "resolved, nothing enabled". `|| []` would collapse the two and hide
         * every optional feature from a mis-planned tenant instead of letting
         * the caller fall back. `undefined` — an older backend that does not
         * send the field — also lands on null, which is the honest answer.
         */
        verticals: data?.verticals || [],
        features: data?.features ?? null,
        /**
         * Which `user` row the embedded traveller app books under — see
         * app-backend service/travellerIdentity.ts. Two ids plus the partner's
         * channel: B2B books under the agency, B2C under the individual.
         *
         * Persisted here rather than re-fetched because this object whitelists
         * what it stores, so anything absent from this list is silently dropped
         * on the next reload — and the frame needs it on first paint.
         */
        travellerIdentity: data?.travellerIdentity || null,
      }),
    );
  } catch {
    // Private-browsing quota or a disabled store. The session stays in memory
    // for this page view; a refresh will land back on the login screen.
  }
};

/**
 * Read a stored session, or null when there is none or its token has expired.
 *
 * The expiry check matters: without it a stale session would rehydrate, render
 * the shell, and then fail every request with a 401 — which reads to the user
 * as the portal being broken rather than as being signed out.
 */
export const loadPortalSession = (portal) => {
  try {
    const raw = sessionStorage.getItem(keyFor(portal));
    if (!raw) return null;

    const session = JSON.parse(raw);
    if (!session?.jwtToken) {
      clearPortalSession(portal);
      return null;
    }

    // The ACCESS token expiring is normal now — it lives for minutes and the
    // refresh token renews it. So an expired access token with a refresh token
    // beside it is a session to RESTORE, not one to discard; discarding it would
    // sign the user out every fifteen minutes.
    if (!isValidJWT(session.jwtToken) && !session.refreshToken) {
      clearPortalSession(portal);
      return null;
    }
    return session;
  } catch {
    clearPortalSession(portal);
    return null;
  }
};

export const clearPortalSession = (portal) => {
  try {
    sessionStorage.removeItem(keyFor(portal));
  } catch {
    /* nothing recoverable */
  }
};

/**
 * Why this tab's session was ended FOR the user rather than BY them — SMB-4352.
 *
 * When the server rejects a session, the transport clears it and the route guard
 * drops the user on the login screen. Without a note, that screen looks exactly
 * like a fresh visit, so a session dying mid-task read as the portal glitching.
 * The transport writes the reason here; the login screen shows it once and
 * clears it. A deliberate logout never writes it.
 *
 * Its own key per portal, next to the session's, so it is tab-scoped the same way
 * and never outlives the tab.
 */
const EXPIRED_NOTICE_KEYS = {
  crm: "_zenvoya_crm_session_ended",
  partner: "_zenvoya_partner_session_ended",
};

const expiredNoticeKeyFor = (portal) => EXPIRED_NOTICE_KEYS[portal] || EXPIRED_NOTICE_KEYS.crm;

export const markPortalSessionExpired = (portal, message) => {
  try {
    sessionStorage.setItem(expiredNoticeKeyFor(portal), message);
  } catch {
    /* the sign-out still happens; only the explanation is lost */
  }
};

export const readPortalSessionExpired = (portal) => {
  try {
    return sessionStorage.getItem(expiredNoticeKeyFor(portal)) || "";
  } catch {
    return "";
  }
};

export const clearPortalSessionExpired = (portal) => {
  try {
    sessionStorage.removeItem(expiredNoticeKeyFor(portal));
  } catch {
    /* nothing recoverable */
  }
};

/** The token to send as x-access-token, or "" when not signed in. */
export const portalToken = (portal) => loadPortalSession(portal)?.jwtToken || "";

/** The refresh token, or "" when there is none. */
export const portalRefreshToken = (portal) => loadPortalSession(portal)?.refreshToken || "";

/** True when the stored access token has expired but a refresh token remains. */
export const accessTokenExpired = (portal) => {
  const s = loadPortalSession(portal);
  if (!s?.jwtToken) return false;
  return !isValidJWT(s.jwtToken) && !!s.refreshToken;
};

/**
 * Replace the token pair in place, keeping the cached user/partner/permissions.
 * Used after a refresh, which returns a fresh context alongside new tokens.
 */
export const updatePortalTokens = (portal, data) => {
  const current = loadPortalSession(portal) || {};
  savePortalSession(portal, { ...current, ...data });
};
